import type { DealerAlarm, DealerEventAlarmFlag } from '../../models/entities/dealer-api.model';
import type {
  EventAiTag,
  EventAlarmIndicator,
  EventAlarmRow,
  EventAlarmsResponse,
} from '../../models/entities/event-detail.model';
import {
  detectionVariant,
  parseClipLocations,
  parseDetectedObjects,
  recognizedVariant,
} from '../dto-mappers';

const INDICATOR_BY_VARIANT: Record<EventAiTag['variant'], EventAlarmIndicator> = {
  motion: 'motion',
  human: 'human',
  vehicle: 'vehicle',
  gun: 'gun',
};

/** Dealer payloads may use camelCase or PascalCase depending on gateway serialization. */
export function extractAlarmDetectedObjects(
  alarm: DealerAlarm | (DealerAlarm & { DetectedObjects?: unknown }),
): string | readonly string[] | null {
  const raw =
    alarm.detectedObjects ??
    ('DetectedObjects' in alarm ? alarm.DetectedObjects : null);
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'string' || Array.isArray(raw)) {
    return raw;
  }
  return String(raw);
}

/** Maps Dealer alarms into the Event View alarm-list contract. */
export function mapDealerAlarmsToEventAlarms(
  alarms: DealerAlarm[] | null | undefined,
): EventAlarmsResponse {
  const items = (alarms ?? []).map((alarm) => {
    const cameraKey = String(Number(alarm.cameraId));
    const { labels, detections } = parseDetectedObjects(extractAlarmDetectedObjects(alarm));
    const tagNames =
      labels.length > 0 ? labels : detections.map((detection) => detection.name).filter(Boolean);
    const aiTags: EventAiTag[] = tagNames.map((label) => ({
      label: label.charAt(0).toUpperCase() + label.slice(1),
      variant: detectionVariant(label, 'motion'),
    }));
    const firstKnown = tagNames.map(recognizedVariant).find((variant) => variant !== null) ?? null;

    return {
      id: alarm.alarmId,
      cameraId: cameraKey,
      // Only a label we actually recognise sets the row indicator; unknown labels still
      // surface as tags but leave the indicator blank rather than guessing.
      indicator: firstKnown ? INDICATOR_BY_VARIANT[firstKnown] : 'none',
      timestamp: alarm.createdDateTime,
      camera: alarm.description?.trim() || `Camera ${cameraKey}`,
      aiTags,
      flagged: false,
      clipUrls: parseClipLocations(alarm.clipUrl),
      detections,
    };
  });

  return { items };
}

/**
 * Appends alarms the baseline does not already contain.
 * A later `alarmStatus=New` poll repeats alarms that are still new; those stay put.
 */
export function mergeNewEventAlarms(
  existing: readonly EventAlarmRow[],
  incoming: readonly EventAlarmRow[],
): EventAlarmRow[] {
  const seen = new Set(existing.map((alarm) => alarm.id));
  const merged = [...existing];
  for (const alarm of incoming) {
    if (seen.has(alarm.id)) {
      continue;
    }
    seen.add(alarm.id);
    merged.push(alarm);
  }
  return merged;
}

/**
 * Sets each row's flagged bit from the flagged-alarms payload.
 * Rows the server no longer lists as flagged become unflagged.
 */
export function replaceFlaggedStateOnAlarms(
  items: readonly EventAlarmRow[],
  flags: DealerEventAlarmFlag[] | null | undefined,
): EventAlarmRow[] {
  const flaggedIds = new Set(
    (flags ?? []).filter((flag) => flag.isFlagged).map((flag) => flag.alarmId),
  );
  return items.map((alarm) => {
    const flagged = flaggedIds.has(alarm.id);
    return alarm.flagged === flagged ? alarm : { ...alarm, flagged };
  });
}

/** Applies server flagged-alarm records onto mapped Event View alarm rows. */
export function applyFlaggedStateToEventAlarms(
  response: EventAlarmsResponse,
  flags: DealerEventAlarmFlag[] | null | undefined,
): EventAlarmsResponse {
  const flaggedIds = new Set(
    (flags ?? []).filter((flag) => flag.isFlagged).map((flag) => flag.alarmId),
  );

  if (flaggedIds.size === 0) {
    return response;
  }

  return {
    items: response.items.map((alarm) =>
      flaggedIds.has(alarm.id) ? { ...alarm, flagged: true } : alarm,
    ),
  };
}
